import type { FactValue } from './fact-values'
import type { CompanyFactReviewReason } from './rejection-reasons'

// ── Enums ───────────────────────────────────────────────────────────

export enum CompanyFactStatus {
  PENDING = 'PENDING',
  UNVERIFIED = 'UNVERIFIED',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  REPLACED = 'REPLACED',
  RETRACTED = 'RETRACTED',
  ARCHIVED = 'ARCHIVED',
}

// PENDING + UNVERIFIED both need ops review (approve/reject). The metric/render
// boundary keeps PENDING-only as the active backlog and UNVERIFIED as muted
// dormant-company state — but for "can the user act on this fact right now?"
// they're equivalent.
export function isReviewableStatus(status: CompanyFactStatus): boolean {
  switch (status) {
    case CompanyFactStatus.PENDING:
    case CompanyFactStatus.UNVERIFIED:
      return true
    case CompanyFactStatus.APPROVED:
    case CompanyFactStatus.REJECTED:
    case CompanyFactStatus.REPLACED:
    case CompanyFactStatus.RETRACTED:
    case CompanyFactStatus.ARCHIVED:
      return false
  }
}

export enum CompanyFactCreationMethod {
  EXTRACTED = 'EXTRACTED',
  MANUAL = 'MANUAL',
  MERGE = 'MERGE',
  DERIVED = 'DERIVED',
}

export enum CompanyFactSourceType {
  FILE_UPLOAD = 'FILE_UPLOAD',
  FACT_MERGER = 'FACT_MERGER',
  OPS_MANUAL_ENTRY = 'OPS_MANUAL_ENTRY',
  SLACK_THREAD = 'SLACK_THREAD',
  CLAUDE_CONNECTOR_AGGREGATION = 'CLAUDE_CONNECTOR_AGGREGATION',
  GOOGLE_DRIVE_BINARY_FILE = 'GOOGLE_DRIVE_BINARY_FILE',
  GOOGLE_WORKSPACE_DOC_EXPORT = 'GOOGLE_WORKSPACE_DOC_EXPORT',
  GMAIL_MESSAGE = 'GMAIL_MESSAGE',
  GMAIL_ATTACHMENT = 'GMAIL_ATTACHMENT',
}

export enum CompanyFactSourceProcessingStatus {
  QUEUED = 'QUEUED',
  PROCESSED = 'PROCESSED',
  FAILED = 'FAILED',
}

export enum CompanyFactCategory {
  BUSINESS_PROFILE = 'BUSINESS_PROFILE',
  PRICING_DETAILS = 'PRICING_DETAILS',
  TIMEKEEPING = 'TIMEKEEPING',
  BILLING_DETAILS = 'BILLING_DETAILS',
  INTERNAL_CONTACTS = 'INTERNAL_CONTACTS',
  EXTERNAL_CONTACTS = 'EXTERNAL_CONTACTS',
  WORK_POLICIES = 'WORK_POLICIES',
  ONBOARDING = 'ONBOARDING',
  ARRIVAL_GUIDELINES = 'ARRIVAL_GUIDELINES',
  FORWARD_FILLING = 'FORWARD_FILLING',
  BACKFILLING = 'BACKFILLING',
  ROLE_SPECIFIC_INFO = 'ROLE_SPECIFIC_INFO',
  FACILITY_INFO = 'FACILITY_INFO',
  CUSTOMER_RULES = 'CUSTOMER_RULES',
}

export enum CompanyFactKey {
  BUSINESS_PROFILE_WORKER_CLASSIFICATION = 'BUSINESS_PROFILE_WORKER_CLASSIFICATION',
  BUSINESS_PROFILE_INDUSTRY = 'BUSINESS_PROFILE_INDUSTRY',
  BUSINESS_PROFILE_PAIN_POINTS = 'BUSINESS_PROFILE_PAIN_POINTS',
  BUSINESS_PROFILE_SUCCESS_METRICS = 'BUSINESS_PROFILE_SUCCESS_METRICS',
  BUSINESS_PROFILE_STAFFING_MODEL = 'BUSINESS_PROFILE_STAFFING_MODEL',
  BUSINESS_PROFILE_ONSITE_STRATEGY = 'BUSINESS_PROFILE_ONSITE_STRATEGY',
  BUSINESS_PROFILE_FACILITY_CONDITIONS = 'BUSINESS_PROFILE_FACILITY_CONDITIONS',
  BUSINESS_PROFILE_CULTURAL_ENVIRONMENT = 'BUSINESS_PROFILE_CULTURAL_ENVIRONMENT',
  BUSINESS_PROFILE_DEMOGRAPHICS = 'BUSINESS_PROFILE_DEMOGRAPHICS',
  BUSINESS_PROFILE_CUSTOMER_PRIORITIES = 'BUSINESS_PROFILE_CUSTOMER_PRIORITIES',
  BUSINESS_PROFILE_NOTES = 'BUSINESS_PROFILE_NOTES',
  PRICING_DETAILS_MARKUP_PERCENTAGE = 'PRICING_DETAILS_MARKUP_PERCENTAGE',
  /** @deprecated Superseded by PRICING_DETAILS_MARKUP_TYPE. Kept so existing DB rows still parse. */
  PRICING_DETAILS_DYNAMIC_MARKUP = 'PRICING_DETAILS_DYNAMIC_MARKUP',
  PRICING_DETAILS_MARKUP_TYPE = 'PRICING_DETAILS_MARKUP_TYPE',
  PRICING_DETAILS_CONVERSION_FEE = 'PRICING_DETAILS_CONVERSION_FEE',
  PRICING_DETAILS_NOTES = 'PRICING_DETAILS_NOTES',
  TIMEKEEPING_CLOCK_IN_METHOD = 'TIMEKEEPING_CLOCK_IN_METHOD',
  /** @deprecated Retired from intake. Kept so existing DB rows still parse. */
  TIMEKEEPING_SCHEDULED_BREAKS = 'TIMEKEEPING_SCHEDULED_BREAKS',
  TIMEKEEPING_TIMESHEET_EMAIL = 'TIMEKEEPING_TIMESHEET_EMAIL',
  TIMEKEEPING_TIMESHEET_SCHEDULE = 'TIMEKEEPING_TIMESHEET_SCHEDULE',
  TIMEKEEPING_TIMESHEET_COMPANY = 'TIMEKEEPING_TIMESHEET_COMPANY',
  TIMEKEEPING_TIME_DISPUTE_PROCESS = 'TIMEKEEPING_TIME_DISPUTE_PROCESS',
  TIMEKEEPING_NOTES = 'TIMEKEEPING_NOTES',
  BILLING_DETAILS_BILLING_EMAIL = 'BILLING_DETAILS_BILLING_EMAIL',
  /** @deprecated Superseded by BILLING_DETAILS_WORK_WEEK_END. Kept so existing DB rows still parse. */
  BILLING_DETAILS_INVOICE_CYCLE_END = 'BILLING_DETAILS_INVOICE_CYCLE_END',
  BILLING_DETAILS_INVOICE_FREQUENCY = 'BILLING_DETAILS_INVOICE_FREQUENCY',
  BILLING_DETAILS_WORK_WEEK_END = 'BILLING_DETAILS_WORK_WEEK_END',
  BILLING_DETAILS_NET_TERMS = 'BILLING_DETAILS_NET_TERMS',
  BILLING_DETAILS_LATE_FEE_POLICY = 'BILLING_DETAILS_LATE_FEE_POLICY',
  BILLING_DETAILS_PAY_SCHEDULE = 'BILLING_DETAILS_PAY_SCHEDULE',
  BILLING_DETAILS_OVERTIME_PAY = 'BILLING_DETAILS_OVERTIME_PAY',
  BILLING_DETAILS_INVOICE_DELIVERY_METHOD = 'BILLING_DETAILS_INVOICE_DELIVERY_METHOD',
  BILLING_DETAILS_INVOICE_CC_EMAILS = 'BILLING_DETAILS_INVOICE_CC_EMAILS',
  BILLING_DETAILS_INVOICE_REQUIREMENTS = 'BILLING_DETAILS_INVOICE_REQUIREMENTS',
  BILLING_DETAILS_NOTES = 'BILLING_DETAILS_NOTES',
  INTERNAL_CONTACTS_COPS = 'INTERNAL_CONTACTS_COPS',
  INTERNAL_CONTACTS_WOPS = 'INTERNAL_CONTACTS_WOPS',
  EXTERNAL_CONTACTS = 'EXTERNAL_CONTACTS',
  WORK_POLICIES_ATTIRE_POLICY = 'WORK_POLICIES_ATTIRE_POLICY',
  WORK_POLICIES_ATTENDANCE_POLICY = 'WORK_POLICIES_ATTENDANCE_POLICY',
  WORK_POLICIES_CALL_OUT_POLICY = 'WORK_POLICIES_CALL_OUT_POLICY',
  WORK_POLICIES_RETURN_POLICY = 'WORK_POLICIES_RETURN_POLICY',
  WORK_POLICIES_HOLIDAYS = 'WORK_POLICIES_HOLIDAYS',
  WORK_POLICIES_COACHING_NOTES = 'WORK_POLICIES_COACHING_NOTES',
  WORK_POLICIES_OT_EXPECTED = 'WORK_POLICIES_OT_EXPECTED',
  WORK_POLICIES_OT_VOLUNTARY_MANDATORY = 'WORK_POLICIES_OT_VOLUNTARY_MANDATORY',
  WORK_POLICIES_NOTES = 'WORK_POLICIES_NOTES',
  ONBOARDING_BACKGROUND_CHECK = 'ONBOARDING_BACKGROUND_CHECK',
  ONBOARDING_DRUG_TEST = 'ONBOARDING_DRUG_TEST',
  ONBOARDING_RESUME_REVIEW = 'ONBOARDING_RESUME_REVIEW',
  ONBOARDING_INTERVIEW = 'ONBOARDING_INTERVIEW',
  ONBOARDING_ONLINE_ORIENTATION = 'ONBOARDING_ONLINE_ORIENTATION',
  ONBOARDING_SITE_ORIENTATION = 'ONBOARDING_SITE_ORIENTATION',
  ONBOARDING_TOUR = 'ONBOARDING_TOUR',
  ONBOARDING_NOTES = 'ONBOARDING_NOTES',
  ONBOARDING_PROCESS_OVERVIEW = 'ONBOARDING_PROCESS_OVERVIEW',
  ONBOARDING_ADDITIONAL_REQUIREMENTS = 'ONBOARDING_ADDITIONAL_REQUIREMENTS',
  ARRIVAL_GUIDELINES_PARKING = 'ARRIVAL_GUIDELINES_PARKING',
  ARRIVAL_GUIDELINES_ENTRANCE = 'ARRIVAL_GUIDELINES_ENTRANCE',
  /** @deprecated Retired from intake. Kept so existing DB rows still parse. */
  ARRIVAL_GUIDELINES_CLOCK_IN = 'ARRIVAL_GUIDELINES_CLOCK_IN',
  ARRIVAL_GUIDELINES_CONTACT_SUPERVISOR = 'ARRIVAL_GUIDELINES_CONTACT_SUPERVISOR',
  ARRIVAL_GUIDELINES_FIRST_DAY = 'ARRIVAL_GUIDELINES_FIRST_DAY',
  ARRIVAL_GUIDELINES_NOTES = 'ARRIVAL_GUIDELINES_NOTES',
  FORWARD_FILLING_OVERBOOKS = 'FORWARD_FILLING_OVERBOOKS',
  FORWARD_FILLING_UNAPPROVED = 'FORWARD_FILLING_UNAPPROVED',
  FORWARD_FILLING_NOTES = 'FORWARD_FILLING_NOTES',
  BACKFILLING_BACKFILLABLE = 'BACKFILLING_BACKFILLABLE',
  BACKFILLING_TIME_LIMIT = 'BACKFILLING_TIME_LIMIT',
  BACKFILLING_PREFERENCES = 'BACKFILLING_PREFERENCES',
  BACKFILLING_TIER_REQUIREMENT = 'BACKFILLING_TIER_REQUIREMENT',
  BACKFILLING_NOTES = 'BACKFILLING_NOTES',
  ROLE_TITLE = 'ROLE_TITLE',
  ROLE_PAY_RATE = 'ROLE_PAY_RATE',
  ROLE_ATTRIBUTE = 'ROLE_ATTRIBUTE',
  /** @deprecated Superseded by ROLE_SCHEDULE_AND_PAY. Kept so existing DB rows still parse. */
  ROLE_PAY_RATE_INFO = 'ROLE_PAY_RATE_INFO',
  /** @deprecated Superseded by ROLE_SCHEDULE_AND_PAY. Kept so existing DB rows still parse. */
  ROLE_SCHEDULE = 'ROLE_SCHEDULE',
  ROLE_SCHEDULE_AND_PAY = 'ROLE_SCHEDULE_AND_PAY',
  ROLE_PHYSICAL_REQUIREMENTS = 'ROLE_PHYSICAL_REQUIREMENTS',
  ROLE_SOFT_SKILLS = 'ROLE_SOFT_SKILLS',
  ROLE_EQUIPMENT = 'ROLE_EQUIPMENT',
  ROLE_LANGUAGE = 'ROLE_LANGUAGE',
  ROLE_GENDER_PREFERENCE = 'ROLE_GENDER_PREFERENCE',
  ROLE_DISCOVERY_NOTES = 'ROLE_DISCOVERY_NOTES',
  FACILITY_ADDRESS = 'FACILITY_ADDRESS',
  CUSTOMER_RULES_BUSINESS_SUPPORT = 'CUSTOMER_RULES_BUSINESS_SUPPORT',
  CUSTOMER_RULES_WORKER_SUPPORT = 'CUSTOMER_RULES_WORKER_SUPPORT',
}

// CARDINALITY IS DUPLICATED — keep these in sync when changing whether a fact key
// is single- or multi-cardinal:
//   1. `conflictStrategy` per key in shared/types/.../company-facts/categories/*.ts
//   2. `FACT_KEY_CARDINALITY` in apps/traba-server-node/src/models/company-facts/types/company-fact-cardinality.types.ts
//   3. `MultiEntryFactKey` / `MultiSelectFactKey` + `MULTI_ENTRY_FACT_HANDLERS`
//      in apps/ops-console/src/screens/CompanyDetailsScreen/components/multiEntryFactHandlers.ts
// shared/types isn't wired into the backend yet, so this can't be derived. Once it is,
// collapse FACT_KEY_CARDINALITY to a derived constant and drop the duplication.
// TODO(ENG-19003): consolidate company-facts cardinality config into a single source of truth.
export enum ConflictStrategy {
  REPLACE = 'REPLACE',
  ALLOW_MULTIPLE = 'ALLOW_MULTIPLE',
  APPEND = 'APPEND',
}

export enum CompanyFactSchemaVersion {
  NUMBER_V1 = 'NUMBER_V1',
  EMAIL_V1 = 'EMAIL_V1',
  SINGLE_SELECT_V1 = 'SINGLE_SELECT_V1',
  SINGLE_SELECT_WITH_NOTES_V1 = 'SINGLE_SELECT_WITH_NOTES_V1',
  YES_NO_WITH_NOTES_V1 = 'YES_NO_WITH_NOTES_V1',
  NUMBER_WITH_NOTES_V1 = 'NUMBER_WITH_NOTES_V1',
  CONVERSION_FEE_V1 = 'CONVERSION_FEE_V1',
  PAY_SCHEDULE_V1 = 'PAY_SCHEDULE_V1',
  PAIN_POINT_V1 = 'PAIN_POINT_V1',
  CONTACT_V1 = 'CONTACT_V1',
  TIER_REQUIREMENT_V1 = 'TIER_REQUIREMENT_V1',
  DRUG_TEST_V1 = 'DRUG_TEST_V1',
  DRUG_TEST_V2 = 'DRUG_TEST_V2',
  CALL_OUT_LINE_V1 = 'CALL_OUT_LINE_V1',
  SCHEDULED_BREAKS_V1 = 'SCHEDULED_BREAKS_V1',
  TEXT_V3 = 'TEXT_V3',
  HOLIDAY_V1 = 'HOLIDAY_V1',
  SUCCESS_METRIC_V1 = 'SUCCESS_METRIC_V1',
  ONSITE_STRATEGY_V1 = 'ONSITE_STRATEGY_V1',
  ADDRESS_V1 = 'ADDRESS_V1',
  OVERTIME_PAY_V1 = 'OVERTIME_PAY_V1',
  TIMESHEET_SCHEDULE_V1 = 'TIMESHEET_SCHEDULE_V1',
}

// ── Core Interfaces ─────────────────────────────────────────────────

export interface CompanyFactSource {
  id: string
  originalFileName?: string
  originalFileUrl?: string
  convertedMarkdownPath?: string
  convertedMarkdownGoogleDriveFileId?: string
  originalSourceGoogleDriveFileId?: string
  opsGoogleWorkspaceFileUrl?: string
  slackThreadUrl?: string
  slackChannelName?: string
  md5Hash: string | null
  sourceType: CompanyFactSourceType
  processingStatus: CompanyFactSourceProcessingStatus
  uploadedBy: string
  createdAt: string
  updatedAt: string
}

export interface CompanyFactSourceResponse extends CompanyFactSource {
  companyId: string
}

export interface CompanyFactCitation {
  id: string
  createdAt: string
  sourceType: CompanyFactSourceType
  sourceId: string
  citation: string
}

export interface CompanyFactClaim {
  id: string
  createdAt: string
  updatedAt: string
  factId: string
  position: number
  text: string
  citations: CompanyFactCitation[]
}

export interface ConflictingFact {
  id: string
  factValue: FactValue
}

export interface CompanyFactRejectionInfo {
  /** Structured reason code. Null for facts rejected before structured reasons were introduced. */
  reasonCode: CompanyFactReviewReason | null
  /** Free-text notes from the reviewer, or legacy free-text reason for pre-structured rejections. */
  notes: string | null
  rejectedAt: string
  rejectedBy: string
}

export interface CompanyFact {
  id: string
  createdAt: string
  updatedAt: string
  companyId: string
  roleId?: string
  locationId?: string
  status: CompanyFactStatus
  category: CompanyFactCategory
  factKey: CompanyFactKey
  schemaVersion: CompanyFactSchemaVersion
  creationMethod: CompanyFactCreationMethod
  /** Parsed fact value returned by the backend */
  factValue: FactValue
  /** @deprecated Use citations instead */
  source?: CompanyFactSource
  citations?: CompanyFactCitation[]
  claims?: CompanyFactClaim[]
  /**
   * Present and non-empty when this fact conflicts with other facts.
   * For MULTI cardinality keys (pain points, success metrics), this will always be undefined.
   * For SINGLE cardinality keys, this contains the facts that conflict with this one.
   */
  conflictingFacts?: ConflictingFact[]
  createdBy?: string
  approvedAt?: string
  approvedBy?: string
  rejection?: CompanyFactRejectionInfo
  replacedAt?: string
  replacedBy?: string
  replacementReason?: string
  unverifiedAt?: string
  unverifiedBy?: string
  unverifiedReason?: string
}

// ── Config Interfaces ───────────────────────────────────────────────

export interface FactKeyConfig {
  schemaVersion: CompanyFactSchemaVersion
  displayName: string
  options?: readonly string[]
  conflictStrategy?: ConflictStrategy
  /** Hover-tooltip explaining when this fact key applies vs adjacent keys. */
  purposeTooltip?: string
}

export interface FactCategoryConfig {
  displayName: string
  keys: CompanyFactKey[]
}
